/**
 * 데스크탑(Tauri) 감지와 IPC 진입점.
 *
 * - 웹 빌드는 이 모듈을 불러도 동작이 바뀌지 않는다. `@tauri-apps/api`는 데스크탑에서만 동적으로 불러온다(T-2.01).
 * - 웹뷰는 세션 토큰을 모른다. 파일 쓰기·외부 링크는 Rust 커맨드가 검증한 뒤에만 한다(§4.5).
 */

export type DesktopPlatform = "macos" | "windows" | "web";

interface TauriInternals {
  invoke?: unknown;
}

export function isDesktop(): boolean {
  return typeof window !== "undefined" && !!(window as unknown as { __TAURI_INTERNALS__?: TauriInternals }).__TAURI_INTERNALS__;
}

export function platform(): DesktopPlatform {
  if (!isDesktop()) return "web";
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  return /Windows/i.test(ua) ? "windows" : "macos";
}

type CoreModule = typeof import("@tauri-apps/api/core");
type EventModule = typeof import("@tauri-apps/api/event");

let core: Promise<CoreModule> | null = null;
let events: Promise<EventModule> | null = null;

function loadCore(): Promise<CoreModule> {
  core ??= import("@tauri-apps/api/core");
  return core;
}

function loadEvents(): Promise<EventModule> {
  events ??= import("@tauri-apps/api/event");
  return events;
}

export class DesktopUnavailableError extends Error {
  constructor() {
    super("데스크탑 앱에서만 쓸 수 있어요");
  }
}

/** Rust 커맨드 호출. 웹에서는 DesktopUnavailableError. */
export async function invoke<T>(cmd: string, args?: Record<string, unknown> | Uint8Array, options?: { headers?: Record<string, string> }): Promise<T> {
  if (!isDesktop()) throw new DesktopUnavailableError();
  const { invoke: tauriInvoke } = await loadCore();
  return tauriInvoke<T>(cmd, args as never, options?.headers ? { headers: options.headers } : undefined);
}

/** Rust 이벤트 구독. 반환한 함수로 해제한다. 웹에서는 아무 일도 하지 않는다. */
export async function listen<T>(event: string, handler: (payload: T) => void): Promise<() => void> {
  if (!isDesktop()) return () => {};
  const { listen: tauriListen } = await loadEvents();
  return tauriListen<T>(event, (e) => handler(e.payload));
}

type WindowModule = typeof import("@tauri-apps/api/window");
let windowMod: Promise<WindowModule> | null = null;

export async function currentWindow() {
  if (!isDesktop()) return null;
  windowMod ??= import("@tauri-apps/api/window");
  return (await windowMod).getCurrentWindow();
}
